/**
 * Custom element for displaying cart upsell products.
 * Fetches complementary products based on cart contents and displays them as upsell cards.
 * Supports both manual product selection and dynamic recommendations.
 *
 * @class CartUpsell
 * @extends {HTMLElement}
 */
class CartUpsell extends HTMLElement {
    /**
     * Creates an instance of CartUpsell.
     * Initializes properties and parses manual product IDs from data attributes.
     */
    constructor() {
        super();
        this.cartProductIds = [];
        this.upsellProductIds = new Set();
        this.maxItems = parseInt(this.dataset.maxItems || '2', 10);
        this.grid = this.querySelector('[data-upsell-grid]');
        this.manualProductIds = this.parseManualProductIds();
        this.renderedProductIds = new Set(this.manualProductIds);
        this.isInitializing = false;
    }

    /**
     * Called when the element is inserted into the DOM.
     * Initializes the component and sets up cart update observers.
     */
    connectedCallback() {
        this.init();
        this.observeCartUpdates();
    }

    /**
     * Parses manual product IDs from the data-manual-product-ids attribute.
     *
     * @returns {number[]} Array of product IDs, or empty array if parsing fails
     */
    parseManualProductIds() {
        try {
            return JSON.parse(this.dataset.manualProductIds || '[]').map(id => parseInt(id, 10)).filter(Boolean);
        } catch {
            return [];
        }
    }

    /**
     * Sets up observers for cart update events.
     * Listens to both PUB_SUB_EVENTS.cartUpdate and custom REBOOTS:cartChange events.
     */
    observeCartUpdates() {
        if (typeof subscribe === 'function') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
                setTimeout(() => this.init(), 100);
            });
        } else {
            document.addEventListener('REBOOTS:cartChange', () => {
                setTimeout(() => this.init(), 100);
            });
        }
    }

    /**
     * Initializes the component by updating cart products and rendering dynamic products.
     * Prevents concurrent executions with a guard flag to avoid race conditions.
     *
     * @async
     */
    async init() {
        // Prevent concurrent initialization to avoid race conditions
        if (this.isInitializing) {
            return;
        }

        this.isInitializing = true;
        try {
            await this.updateCartProducts();
            await this.renderDynamicProducts();
            this.updateVisibility();
        } finally {
            this.isInitializing = false;
            this.dispatchEvent(new CustomEvent('rb:cart-upsell:ready', { bubbles: true }));
        }
    }

    /**
     * Fetches current cart data and updates internal product ID lists.
     * Stores both regular cart product IDs and upsell product IDs (items with _upsell_product property).
     *
     * @async
     */
    async updateCartProducts() {
        try {
            const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
            const cart = await response.json();
            // Store cart product IDs to filter them out from upsell suggestions
            this.cartProductIds = cart.items.map(item => item.product_id).filter(Boolean);
            // Store items that are upsells (have _upsell_product property) - we don't need recommendations for these
            this.upsellProductIds = new Set(
                cart.items
                    .filter(item => {
                        if (!item.properties) return false;
                        // Properties can be an array of [key, value] pairs or an object
                        if (Array.isArray(item.properties)) {
                            return item.properties.some(prop =>
                                Array.isArray(prop) && prop[0] === '_upsell_product' && prop[1]
                            );
                        } else if (typeof item.properties === 'object') {
                            return item.properties._upsell_product != null;
                        }
                        return false;
                    })
                    .map(item => item.product_id)
                    .filter(Boolean)
            );
        } catch {
            this.cartProductIds = [];
            this.upsellProductIds = new Set();
        }
    }

    /**
     * Renders dynamic product recommendations based on cart contents.
     * Removes products that are already in cart, filters out upsell products,
     * and fetches complementary products to fill available slots.
     *
     * @async
     */
    async renderDynamicProducts() {
        if (!this.grid || this.cartProductIds.length === 0) {
            return;
        }

        // Get manual products already in DOM
        const manualCards = Array.from(this.grid.querySelectorAll('[data-upsell-manual="true"]'));

        // Filter out manual products that are already in cart and remove their cards
        const manualIdsNotInCart = [];
        manualCards.forEach(card => {
            const productId = parseInt(card.dataset.productId || '', 10);
            if (productId && this.cartProductIds.includes(productId)) {
                card.remove();
            } else if (productId) {
                manualIdsNotInCart.push(productId);
            }
        });

        this.renderedProductIds = new Set(manualIdsNotInCart);

        // Calculate available slots
        const availableSlots = this.maxItems - manualIdsNotInCart.length;
        if (availableSlots <= 0) return;

        // Remove old dynamic cards
        const dynamicCards = this.grid.querySelectorAll('[data-upsell-card]:not([data-upsell-manual="true"])');
        dynamicCards.forEach(card => card.remove());

        // Filter out upsell products - we don't need recommendations for products that are already upsells
        const productIdsForRecommendations = this.cartProductIds
            .filter(id => !this.upsellProductIds.has(id))
            .slice(0, 3);

        // Only fetch if we have product IDs that are not upsells
        if (productIdsForRecommendations.length === 0) return;

        // Fetch complementary products
        const products = await this.fetchComplementaryProducts(productIdsForRecommendations, availableSlots);
        if (!products.length) return;

        // Filter and render unique products that are available and not already in cart
        const uniqueProducts = [];
        const seen = new Set();

        for (const product of products) {
            // Skip if product is invalid, already seen, already rendered, or already in cart
            if (!product ||
                !product.id ||
                seen.has(product.id) ||
                this.renderedProductIds.has(product.id) ||
                this.cartProductIds.includes(product.id)) {
                continue;
            }

            // Only include products with available variants
            if (product.variants?.some(v => v.available)) {
                seen.add(product.id);
                uniqueProducts.push(product);
                if (uniqueProducts.length >= availableSlots) break;
            }
        }

        // Render cards in parallel
        const cardPromises = uniqueProducts.map(product =>
            this.prependProductCard(product).then(() => {
                this.renderedProductIds.add(product.id);
            })
        );

        await Promise.all(cardPromises);
    }

    /**
     * Fetches complementary products from Shopify's recommendations API.
     * Fetches recommendations for multiple products in parallel using Promise.allSettled().
     *
     * @param {number[]} productIds - Array of product IDs to get recommendations for
     * @param {number} [maxProducts=6] - Maximum number of products to fetch
     * @returns {Promise<Object[]>} Array of product objects
     * @async
     */
    async fetchComplementaryProducts(productIds, maxProducts = 6) {
        // Fetch all recommendations in parallel
        const fetchPromises = productIds.map(productId =>
            fetch(`/recommendations/products.json?product_id=${productId}&limit=${maxProducts}&intent=complementary`)
                .then(response => {
                    if (!response.ok) return null;
                    return response.json();
                })
                .then(data => {
                    if (Array.isArray(data?.products)) {
                        return data.products;
                    }
                    return null;
                })
                .catch(() => null)
        );

        // Wait for all requests to complete (parallel execution)
        const results = await Promise.allSettled(fetchPromises);

        // Collect all products from successful responses
        const allProducts = [];
        for (const result of results) {
            if (result.status === 'fulfilled' && result.value) {
                allProducts.push(...result.value);
            }
        }

        // Return unique products, limited to maxProducts
        const uniqueProducts = [];
        const seen = new Set();
        for (const product of allProducts) {
            if (product && product.id && !seen.has(product.id)) {
                seen.add(product.id);
                uniqueProducts.push(product);
                if (uniqueProducts.length >= maxProducts) {
                    break;
                }
            }
        }

        return uniqueProducts;
    }

    /**
     * Fetches and prepends a product card to the upsell grid.
     * Uses the rb-cartupsellcard product template view.
     *
     * @param {Object} product - Product object with handle property
     * @returns {Promise<void>}
     * @async
     */
    async prependProductCard(product) {
        try {
            const response = await fetch(`/products/${encodeURIComponent(product.handle)}?view=rb-cartupsellcard`);
            if (!response.ok) return;

            const html = await response.text();
            const template = document.createElement('template');
            template.innerHTML = html.trim();
            const card = template.content.firstElementChild;

            if (card) {
                card.dataset.upsellManual = 'false';
                this.grid.prepend(card);
            }
        } catch {
            // Silently fail
        }
    }

    /**
     * Updates the visibility of the upsell section based on whether there are cards and cart items.
     * Hides the section if there are no cards or no cart items.
     */
    updateVisibility() {
        const hasCards = this.grid?.querySelector('[data-upsell-card]');
        const hasCartItems = this.cartProductIds.length > 0;
        this.toggleAttribute('hidden', !(hasCards && hasCartItems));
    }
}

if (!customElements.get('cart-upsell')) {
    customElements.define('cart-upsell', CartUpsell);
}
